import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { Pagination } from "../../components/crm/Pagination";
import { RequirePermission } from "../../components/crm/RequirePermission";
import {
  SupportChannelLabel,
  SupportPriorityBadge,
  SupportStatusBadge,
} from "../../components/support/SupportBadges";
import { Spinner } from "../../components/Spinner";
import { formatDate } from "../../lib/format";
import { isApiError, listTickets } from "../../lib/support-api";
import type { Ticket, TicketListParams, TicketPriority, TicketStatus } from "../../lib/support-types";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

interface Filters {
  q: string;
  status: "" | TicketStatus;
  priority: "" | TicketPriority;
  assignee: string;
}

const EMPTY_FILTERS: Filters = { q: "", status: "", priority: "", assignee: "" };
const PAGE_LIMIT = 25;

function SupportTicketsInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canCreate = hasPermission("support.ticket.create");

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [assigneeInput, setAssigneeInput] = useState("");
  const [items, setItems] = useState<Ticket[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params: TicketListParams = { limit: PAGE_LIMIT, cursor };
      if (filters.q !== "") params.q = filters.q;
      if (filters.status !== "") params.status = filters.status;
      if (filters.priority !== "") params.priority = filters.priority;
      if (filters.assignee !== "") params.assignee_user_id = filters.assignee;
      const page = await listTickets(params);
      setItems(page.items);
      setNextCursor(page.next_cursor);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [cursor, filters, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetCursor = (): void => {
    setCursor(undefined);
    setCursorStack([]);
  };

  const onFilter = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    resetCursor();
    setFilters({
      q: search.trim(),
      status: filters.status,
      priority: filters.priority,
      assignee: assigneeInput.trim(),
    });
  };

  const onClear = (): void => {
    setSearch("");
    setAssigneeInput("");
    resetCursor();
    setFilters(EMPTY_FILTERS);
  };

  const locale = lang === "es" ? "es-ES" : "en-US";
  const hasPrev = cursorStack.length > 0;

  return (
    <section className="page" aria-labelledby="support-tickets-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="support-tickets-title">{t("support.tickets.title")}</h1>
          <p className="muted">{t("support.tickets.subtitle")}</p>
        </div>
        {canCreate ? (
          <Link className="btn btn--primary" to="/support/tickets/new">
            {t("support.tickets.new")}
          </Link>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <form className="filters" onSubmit={onFilter}>
        <label className="field" htmlFor="support-tickets-q">
          <span>{t("support.common.search")}</span>
          <input
            id="support-tickets-q"
            value={search}
            placeholder={t("support.tickets.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="field" htmlFor="support-tickets-status">
          <span>{t("support.filters.status")}</span>
          <select
            id="support-tickets-status"
            value={filters.status}
            onChange={(event) => {
              resetCursor();
              setFilters((current) => ({
                ...current,
                status: event.target.value as Filters["status"],
              }));
            }}
          >
            <option value="">{t("support.common.all")}</option>
            <option value="new">{t("support.status.new")}</option>
            <option value="open">{t("support.status.open")}</option>
            <option value="pending">{t("support.status.pending")}</option>
            <option value="resolved">{t("support.status.resolved")}</option>
            <option value="closed">{t("support.status.closed")}</option>
          </select>
        </label>
        <label className="field" htmlFor="support-tickets-priority">
          <span>{t("support.filters.priority")}</span>
          <select
            id="support-tickets-priority"
            value={filters.priority}
            onChange={(event) => {
              resetCursor();
              setFilters((current) => ({
                ...current,
                priority: event.target.value as Filters["priority"],
              }));
            }}
          >
            <option value="">{t("support.common.all")}</option>
            <option value="low">{t("support.priority.low")}</option>
            <option value="medium">{t("support.priority.medium")}</option>
            <option value="high">{t("support.priority.high")}</option>
            <option value="urgent">{t("support.priority.urgent")}</option>
          </select>
        </label>
        <label className="field" htmlFor="support-tickets-assignee">
          <span>{t("support.filters.assignee")}</span>
          <input
            id="support-tickets-assignee"
            value={assigneeInput}
            aria-describedby="support-tickets-assignee-hint"
            onChange={(event) => setAssigneeInput(event.target.value)}
          />
          <small id="support-tickets-assignee-hint" className="field__hint">
            {t("support.filters.assigneeHint")}
          </small>
        </label>
        <div className="filters__actions">
          <button type="submit" className="btn btn--primary">
            {t("support.common.apply")}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClear}>
            {t("support.common.clear")}
          </button>
        </div>
      </form>

      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("support.tickets.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("support.tickets.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("support.ticket.subject")}</th>
                <th scope="col">{t("support.ticket.status")}</th>
                <th scope="col">{t("support.ticket.priority")}</th>
                <th scope="col">{t("support.ticket.channel")}</th>
                <th scope="col">{t("support.ticket.assignee")}</th>
                <th scope="col">{t("support.ticket.created")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("support.common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((ticket) => (
                <tr key={ticket.id}>
                  <td>
                    <Link to={`/support/tickets/${ticket.id}`}>{ticket.subject}</Link>
                  </td>
                  <td>
                    <SupportStatusBadge status={ticket.status} />
                  </td>
                  <td>
                    <SupportPriorityBadge priority={ticket.priority} />
                  </td>
                  <td>
                    <SupportChannelLabel channel={ticket.channel} />
                  </td>
                  <td>{ticket.assignee_user_id ?? t("support.ticket.assigneeNone")}</td>
                  <td>{formatDate(ticket.created_at, locale)}</td>
                  <td className="table__actions-col">
                    <Link className="btn btn--ghost btn--sm" to={`/support/tickets/${ticket.id}`}>
                      {t("support.common.view")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Pagination
        hasPrev={hasPrev}
        hasNext={nextCursor !== null}
        busy={loading}
        onPrev={() => {
          if (cursorStack.length === 0) {
            return;
          }
          const previous = cursorStack[cursorStack.length - 1];
          setCursorStack((stack) => stack.slice(0, -1));
          setCursor(previous);
        }}
        onNext={() => {
          if (nextCursor === null) {
            return;
          }
          setCursorStack((stack) => [...stack, cursor]);
          setCursor(nextCursor);
        }}
      />
    </section>
  );
}

export function SupportTicketsPage(): ReactNode {
  return (
    <RequirePermission permission="support.ticket.read">
      <SupportTicketsInner />
    </RequirePermission>
  );
}